import { HttpClient } from '@angular/common/http';
import { AuthService } from '../auth/auth.service';
import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../notification/notification.service';
import { catchError, forkJoin, of } from 'rxjs';

type BillingoDocumentInsertType = 'invoice' | 'draft';

interface BillingoSettingsResponse {
  enabled: boolean;
  has_api_key: boolean;
  api_key_masked: string;
  document_insert_type: BillingoDocumentInsertType;
  etsy_invoice_block_id: number | null;
  shopify_hu_invoice_block_id: number | null;
  shopify_en_invoice_block_id: number | null;
  updated_at?: string;
}

interface BillingoInvoiceBlockOption {
  id: number;
  name: string;
  prefix?: string;
  type?: string;
}

// Okos Könyvelőm (app.okoskonyvelom.hu) — a Billingót váltó számlázó. A „kategória" itt az,
// ami a Billingóban a számlatömb volt: forrásonként (Etsy / Shopify HU / Shopify EN) külön.
interface OkosKonyvelomSettingsResponse {
  enabled: boolean;
  base_url: string;
  has_api_key: boolean;
  api_key_masked: string;
  etsy_category_id: string | null;
  shopify_hu_category_id: string | null;
  shopify_en_category_id: string | null;
  updated_at?: string;
}

interface OkosKonyvelomCategoryOption {
  id: string;
  name: string;
  color?: string;
  direction?: string | null;
}

type InvoiceProviderKey = 'billingo' | 'okoskonyvelom';

interface ShippyProSettingsResponse {
  has_api_key: boolean;
  api_key_masked: string;
  api_orders_id: number | null;
  updated_at?: string;
}

interface AfterShipSettingsResponse {
  has_api_key: boolean;
  api_key_masked: string;
  has_webhook_secret: boolean;
  updated_at?: string;
}

interface MailSettingsResponse {
  host: string;
  port: number | null;
  secure: boolean;
  username: string;
  has_password: boolean;
  from_email: string;
  from_name: string;
  configured: boolean;
  updated_at?: string;
}

interface FoxiSettingsResponse {
  has_api_key: boolean;
  api_key_masked: string;
  model: string;
  models: string[];
  enabled: boolean;
  daily_budget_usd: number;
  today_cost_usd: number;
  month_cost_usd: number;
  bot_user_id?: number | null;
}

interface UpsConfigView {
  shipper_name?: string;
  shipper_attention?: string;
  shipper_phone?: string;
  shipper_email?: string;
  shipper_street1?: string;
  shipper_street2?: string;
  shipper_city?: string;
  shipper_zip?: string;
  shipper_country?: string;
  shipper_tax_id?: string;
  default_service_code?: string;
  default_duty_payer?: 'receiver' | 'shipper';
  default_hs_code?: string;
  default_origin_country?: string;
  default_length_cm?: number;
  default_width_cm?: number;
  default_height_cm?: number;
  default_weight_kg?: number;
  label_format?: 'ZPL' | 'GIF';
}

interface FoxpostSettingsResponse {
  configured: boolean;
  username: string;
  has_password: boolean;
  has_api_key: boolean;
  api_key_masked: string;
  environment: 'production' | 'test';
  config: { default_size: string; label_size: string };
}

interface UpsSettingsResponse {
  configured: boolean;
  has_client_id: boolean;
  client_id_masked: string;
  has_client_secret: boolean;
  account_number: string;
  environment: 'production' | 'test';
  config: UpsConfigView;
}

interface SeventeenTrackSettingsResponse {
  has_api_key: boolean;
  api_key_masked: string;
  updated_at?: string;
}

interface MedusaSettingsResponse {
  enabled: boolean;
  base_url: string;
  has_api_key: boolean;
  api_key_masked: string;
  last_sync_at?: string | null;
  last_sync_result?: string | null;
}

interface SeventeenTrackQuotaResponse {
  configured: boolean;
  quota: any;
  error?: string;
}

interface AfterShipCouriersResponse {
  configured: boolean;
  couriers: { slug: string; name: string }[];
  error?: string;
}

// Kalkulációs beállítások — a design 13-beallitasok.html + 23-logic.js settingsVals() alapján.
// Várt backend kontrakt: GET|PATCH `settings/calculation` (lásd záró jelentés).
interface FeeShopRow {
  name: string;
  txn: string;
  pay: string;
  fixed: string;
  listing: string;
}

interface RateRow {
  country: string;
  rate: string;
}

interface CalculationSettingsResponse {
  feeShops: { name: string; txn: number; pay: number; fixed: number; listing: number }[];
  vatRates: { country: string; rate: number }[];
  dutyRates: { country: string; rate: number }[];
  printPerMin: number;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss'
})
export class SettingsComponent implements OnInit {
  loading = false;
  integrationsLoadFailed = false;
  savingBillingo = false;
  savingOkosKonyvelom = false;
  savingShippyPro = false;

  /** Melyik számlázón készülnek az ÚJ számlák. null = egyik sincs bekapcsolva. */
  activeInvoiceProvider: InvoiceProviderKey | null = null;

  // --- Kalkuláció (design 13-beallitasok) ---
  calcLoading = false;
  calcLoadFailed = false;
  savingCalc = false;
  feeShops: FeeShopRow[] = [];
  vatRates: RateRow[] = [];
  dutyRates: RateRow[] = [];
  printPerMin = '';

  // --- Billingo ---
  billingoSettings: BillingoSettingsResponse = {
    enabled: false,
    has_api_key: false,
    api_key_masked: '',
    document_insert_type: 'invoice',
    etsy_invoice_block_id: null,
    shopify_hu_invoice_block_id: null,
    shopify_en_invoice_block_id: null,
  };

  billingoApiKeyInput = '';
  clearApiKey = false;
  billingoBlocks: BillingoInvoiceBlockOption[] = [];
  /** A selectekben megjelenő tömb-opciók: a betöltött lista + a beállított, de a listában
   *  nem szereplő tömb-ID-k (különben a select üresen — selectedIndex=-1 — jelenne meg). */
  billingoBlockOptions: BillingoInvoiceBlockOption[] = [];

  // --- Okos Könyvelőm ---
  okosSettings: OkosKonyvelomSettingsResponse = {
    enabled: false,
    base_url: '',
    has_api_key: false,
    api_key_masked: '',
    etsy_category_id: null,
    shopify_hu_category_id: null,
    shopify_en_category_id: null,
  };

  okosApiKeyInput = '';
  okosBaseUrlInput = '';
  clearOkosApiKey = false;
  okosCategories: OkosKonyvelomCategoryOption[] = [];
  /** A selectek opciói: a betöltött lista + a beállított, de a listában nem szereplő azonosítók
   *  (különben a select üresen — selectedIndex=-1 — jelenne meg egy törölt kategóriánál). */
  okosCategoryOptions: OkosKonyvelomCategoryOption[] = [];

  // --- ShippyPro ---
  shippyProSettings: ShippyProSettingsResponse = {
    has_api_key: false,
    api_key_masked: '',
    api_orders_id: null,
  };

  shippyProApiKeyInput = '';
  shippyProApiOrdersIdInput: string | number | null = '';
  clearShippyProApiKey = false;
  clearShippyProApiOrdersId = false;

  // --- E-mail (SMTP) — határidő-értesítők ---
  mailSettings: MailSettingsResponse = { host: '', port: null, secure: false, username: '', has_password: false, from_email: '', from_name: '', configured: false };
  mailForm = { host: '', port: '' as string | number, secure: false, username: '', password: '', from_email: '', from_name: '' };
  clearMailPassword = false;
  savingMail = false;
  mailTestTo = '';
  sendingMailTest = false;

  // --- Foxpost ---

  private applyFoxpostSettings(settings: FoxpostSettingsResponse | null) {
    this.foxpostSettings = settings;
    if (!settings) return;
    this.foxpostUsernameInput = settings.username || '';
    this.foxpostEnvironmentInput = settings.environment || 'production';
    this.foxpostDefaultSize = settings.config?.default_size || 's';
    this.foxpostLabelSize = settings.config?.label_size || 'A6';
    this.foxpostPasswordInput = '';
    this.foxpostApiKeyInput = '';
  }

  saveFoxpostSettings() {
    const payload: Record<string, unknown> = {
      username: this.foxpostUsernameInput.trim(),
      environment: this.foxpostEnvironmentInput,
      config: { default_size: this.foxpostDefaultSize, label_size: this.foxpostLabelSize },
    };
    if (this.foxpostPasswordInput) payload['password'] = this.foxpostPasswordInput;
    if (this.foxpostApiKeyInput.trim()) payload['api_key'] = this.foxpostApiKeyInput.trim();
    this.savingFoxpost = true;
    this.http.patch<FoxpostSettingsResponse>('integrations/foxpost-settings', payload).subscribe((result) => {
      this.savingFoxpost = false;
      this.applyFoxpostSettings(result);
      this.notificationService.show('Foxpost beállítások mentve.', 'success');
    }, (error) => {
      this.savingFoxpost = false;
      this.notificationService.show(`Foxpost beállítás mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  testFoxpostConnection() {
    this.testingFoxpost = true;
    this.http.post<{ ok: boolean; environment: string }>('integrations/foxpost-settings/test', {}).subscribe((result) => {
      this.testingFoxpost = false;
      this.notificationService.show(`Foxpost kapcsolat rendben (${result.environment === 'test' ? 'teszt' : 'éles'} környezet).`, 'success');
    }, (error) => {
      this.testingFoxpost = false;
      this.notificationService.show(`Foxpost kapcsolat sikertelen: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  // --- UPS ---

  private applyUpsSettings(settings: UpsSettingsResponse | null) {
    this.upsSettings = settings;
    if (!settings) return;
    this.upsAccountInput = settings.account_number || '';
    this.upsEnvironmentInput = settings.environment || 'production';
    this.upsConfig = { ...(settings.config || {}) };
    this.upsClientIdInput = '';
    this.upsClientSecretInput = '';
  }

  saveUpsSettings() {
    const payload: Record<string, unknown> = {
      account_number: this.upsAccountInput.trim(),
      environment: this.upsEnvironmentInput,
      config: this.upsConfig,
    };
    if (this.upsClientIdInput.trim()) payload['client_id'] = this.upsClientIdInput.trim();
    if (this.upsClientSecretInput.trim()) payload['client_secret'] = this.upsClientSecretInput.trim();
    this.savingUps = true;
    this.http.patch<UpsSettingsResponse>('integrations/ups-settings', payload).subscribe((result) => {
      this.savingUps = false;
      this.applyUpsSettings(result);
      this.notificationService.show('UPS beállítások mentve.', 'success');
    }, (error) => {
      this.savingUps = false;
      this.notificationService.show(`UPS beállítás mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  testUpsConnection() {
    this.testingUps = true;
    this.http.post<{ ok: boolean; environment: string }>('integrations/ups-settings/test', {}).subscribe((result) => {
      this.testingUps = false;
      this.notificationService.show(`UPS kapcsolat rendben (${result.environment === 'test' ? 'teszt' : 'éles'} környezet).`, 'success');
    }, (error) => {
      this.testingUps = false;
      this.notificationService.show(`UPS kapcsolat sikertelen: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  // --- Jump & Hide webshop (Medusa) ---
  medusaSettings: MedusaSettingsResponse = { enabled: false, base_url: '', has_api_key: false, api_key_masked: '' };
  medusaBaseUrlInput = '';
  medusaApiKeyInput = '';
  medusaEnabledInput = false;
  savingMedusa = false;
  syncingMedusa = false;

  // --- Foxpost (belföldi címke) ---
  foxpostSettings: FoxpostSettingsResponse | null = null;
  foxpostUsernameInput = '';
  foxpostPasswordInput = '';
  foxpostApiKeyInput = '';
  foxpostEnvironmentInput: 'production' | 'test' = 'production';
  foxpostDefaultSize = 's';
  foxpostLabelSize = 'A6';
  savingFoxpost = false;
  testingFoxpost = false;

  // --- UPS (közvetlen címkézés) ---
  upsSettings: UpsSettingsResponse | null = null;
  upsClientIdInput = '';
  upsClientSecretInput = '';
  upsAccountInput = '';
  upsEnvironmentInput: 'production' | 'test' = 'production';
  upsConfig: UpsConfigView = {};
  savingUps = false;
  testingUps = false;
  readonly upsServiceOptions = [
    { code: '11', name: 'UPS Standard' },
    { code: '65', name: 'UPS Worldwide Saver' },
    { code: '07', name: 'UPS Worldwide Express' },
    { code: '08', name: 'UPS Worldwide Expedited' },
    { code: '54', name: 'UPS Worldwide Express Plus' },
  ];

  // --- 17TRACK (nyomkövetés, elsődleges) ---
  foxiSettings: FoxiSettingsResponse = { has_api_key: false, api_key_masked: '', model: 'claude-sonnet-5-5', models: [], enabled: true, daily_budget_usd: 5, today_cost_usd: 0, month_cost_usd: 0 };
  foxiApiKeyInput = '';
  clearFoxiApiKey = false;
  foxiModelInput = 'claude-sonnet-5-5';
  foxiBudgetInput = 5;
  foxiEnabledInput = true;
  savingFoxi = false;
  seventeenSettings: SeventeenTrackSettingsResponse = { has_api_key: false, api_key_masked: '' };
  seventeenApiKeyInput = '';
  clearSeventeenApiKey = false;
  savingSeventeen = false;
  seventeenQuota: SeventeenTrackQuotaResponse = { configured: false, quota: null };
  seventeenQuotaLoading = false;

  // --- AfterShip (nyomkövetés) ---
  afterShipSettings: AfterShipSettingsResponse = {
    has_api_key: false,
    api_key_masked: '',
    has_webhook_secret: false,
  };
  afterShipApiKeyInput = '';
  afterShipWebhookSecretInput = '';
  clearAfterShipApiKey = false;
  clearAfterShipWebhookSecret = false;
  savingAfterShip = false;
  afterShipCouriers: AfterShipCouriersResponse = { configured: false, couriers: [] };
  afterShipCouriersLoading = false;

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.fetchSettings();
    this.fetchCalculationSettings();
  }

  // ==========================================================================
  // Kalkulációs beállítások
  // ==========================================================================

  fetchCalculationSettings() {
    this.calcLoading = true;
    this.calcLoadFailed = false;
    this.http.get<CalculationSettingsResponse>('settings/calculation').subscribe({
      next: (result) => {
        this.calcLoading = false;
        this.applyCalculationSettings(result);
      },
      error: () => {
        this.calcLoading = false;
        this.calcLoadFailed = true;
      },
    });
  }

  saveCalculationSettings() {
    const num = (v: string) => {
      const parsed = parseFloat(v);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    if (!this.validateCalculationSettings()) {
      return;
    }

    const payload: CalculationSettingsResponse = {
      feeShops: this.feeShops.map((f) => ({
        name: f.name.trim(),
        txn: num(f.txn),
        pay: num(f.pay),
        fixed: num(f.fixed),
        listing: num(f.listing),
      })),
      vatRates: this.vatRates.map((v) => ({ country: v.country.trim().toUpperCase(), rate: num(v.rate) })),
      dutyRates: this.dutyRates.map((d) => ({ country: d.country.trim().toUpperCase(), rate: num(d.rate) })),
      printPerMin: num(this.printPerMin),
    };

    this.savingCalc = true;
    this.http.patch<CalculationSettingsResponse>('settings/calculation', payload).subscribe({
      next: (result) => {
        this.savingCalc = false;
        this.calcLoadFailed = false;
        this.applyCalculationSettings(result);
        this.notificationService.show('Kalkulációs beállítások mentve.', 'success');
      },
      error: (error) => {
        this.savingCalc = false;
        this.notificationService.show(`Kalkulációs beállítások mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
      },
    });
  }

  /** Mentés előtti ellenőrzés: üres webshop-név és nem ISO ország-kód ne kerüljön be
   *  (az országkód a riportok/profit számításánál kulcs — a szemét sosem találna). */
  private validateCalculationSettings(): boolean {
    const emptyShop = this.feeShops.findIndex((f) => !f.name.trim());
    if (emptyShop >= 0) {
      this.notificationService.show(`A(z) ${emptyShop + 1}. webshop neve nem lehet üres.`, 'warning');
      return false;
    }

    const check = (rows: RateRow[], label: string): boolean => {
      const bad = rows.findIndex((r) => !/^[A-Z]{2}$/.test(r.country.trim().toUpperCase()));
      if (bad >= 0) {
        const value = rows[bad].country.trim();
        this.notificationService.show(
          value
            ? `Érvénytelen ${label} országkód: „${value}” — kétbetűs ISO kód kell (pl. DE).`
            : `A(z) ${bad + 1}. ${label} sor országkódja üres.`,
          'warning',
        );
        return false;
      }
      return true;
    };

    return check(this.vatRates, 'ÁFA') && check(this.dutyRates, 'vám');
  }

  // 23-logic.js: num = (e) => e.target.value.replace(/[^0-9.]/g, '')
  onFeeNameChange(event: Event, row: FeeShopRow) {
    row.name = (event.target as HTMLInputElement).value;
  }

  onFeeNumChange(event: Event, row: FeeShopRow, key: 'txn' | 'pay' | 'fixed' | 'listing') {
    row[key] = this.sanitizeNumInput(event);
  }

  onRateCountryChange(event: Event, row: RateRow) {
    const input = event.target as HTMLInputElement;
    const upper = input.value.toUpperCase();
    input.value = upper;
    row.country = upper;
  }

  onRateChange(event: Event, row: RateRow) {
    row.rate = this.sanitizeNumInput(event);
  }

  onPrintPerMinChange(event: Event) {
    this.printPerMin = this.sanitizeNumInput(event);
  }

  addFeeShop() {
    this.feeShops.push({ name: 'Új webshop', txn: '0', pay: '0', fixed: '0', listing: '0' });
  }

  removeFeeShop(index: number) {
    this.feeShops.splice(index, 1);
  }

  addVatRate() {
    this.vatRates.push({ country: '', rate: '0' });
  }

  removeVatRate(index: number) {
    this.vatRates.splice(index, 1);
  }

  addDutyRate() {
    this.dutyRates.push({ country: '', rate: '0' });
  }

  removeDutyRate(index: number) {
    this.dutyRates.splice(index, 1);
  }

  private sanitizeNumInput(event: Event): string {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^0-9.]/g, '');
    input.value = cleaned;
    return cleaned;
  }

  private applyCalculationSettings(result: CalculationSettingsResponse) {
    this.feeShops = (result?.feeShops || []).map((f) => ({
      name: f.name ?? '',
      txn: `${f.txn ?? 0}`,
      pay: `${f.pay ?? 0}`,
      fixed: `${f.fixed ?? 0}`,
      listing: `${f.listing ?? 0}`,
    }));
    this.vatRates = (result?.vatRates || []).map((v) => ({ country: v.country ?? '', rate: `${v.rate ?? 0}` }));
    this.dutyRates = (result?.dutyRates || []).map((d) => ({ country: d.country ?? '', rate: `${d.rate ?? 0}` }));
    this.printPerMin = `${result?.printPerMin ?? ''}`;
  }

  // ==========================================================================
  // Integrációk (Billingo + ShippyPro) — megőrzött funkciók
  // ==========================================================================

  fetchSettings() {
    this.loading = true;
    this.integrationsLoadFailed = false;
    forkJoin({
      billingo: this.http.get<BillingoSettingsResponse>('integrations/billingo-settings'),
      shippyPro: this.http.get<ShippyProSettingsResponse>('integrations/shippypro-settings'),
      mail: this.http
        .get<MailSettingsResponse>('integrations/mail-settings')
        .pipe(catchError(() => of(this.mailSettings))),
      foxi: this.http
        .get<FoxiSettingsResponse>('agent/settings')
        .pipe(catchError(() => of(this.foxiSettings))),
      seventeen: this.http
        .get<SeventeenTrackSettingsResponse>('integrations/seventeentrack-settings')
        .pipe(catchError(() => of({ has_api_key: false, api_key_masked: '' } as SeventeenTrackSettingsResponse))),
      ups: this.http
        .get<UpsSettingsResponse>('integrations/ups-settings')
        .pipe(catchError(() => of(null))),
      foxpost: this.http
        .get<FoxpostSettingsResponse>('integrations/foxpost-settings')
        .pipe(catchError(() => of(null))),
      medusa: this.http
        .get<MedusaSettingsResponse>('medusa/settings')
        .pipe(catchError(() => of(this.medusaSettings))),
      billingoBlocks: this.fetchBillingoBlocksRequest(),
      okos: this.http.get<OkosKonyvelomSettingsResponse>('integrations/okoskonyvelom-settings'),
      okosCategories: this.fetchOkosCategoriesRequest(),
      provider: this.http
        .get<{ provider: InvoiceProviderKey | null }>('integrations/invoice-provider')
        .pipe(catchError(() => of({ provider: null }))),
    }).subscribe((result) => {
      this.loading = false;
      this.billingoSettings = result.billingo;
      this.shippyProSettings = result.shippyPro;
      this.applyMailSettings(result.mail);
      this.applyFoxiSettings(result.foxi);
      this.seventeenSettings = result.seventeen;
      this.fetchSeventeenQuota();
      this.applyMedusaSettings(result.medusa);
      this.applyUpsSettings(result.ups);
      this.applyFoxpostSettings(result.foxpost);
      this.billingoBlocks = result.billingoBlocks;
      this.okosSettings = result.okos;
      this.okosBaseUrlInput = result.okos.base_url || '';
      this.okosCategories = result.okosCategories;
      this.activeInvoiceProvider = result.provider?.provider ?? null;
      this.rebuildBillingoBlockOptions();
      this.rebuildOkosCategoryOptions();
      this.shippyProApiOrdersIdInput = result.shippyPro.api_orders_id || '';
    }, (error) => {
      this.loading = false;
      this.integrationsLoadFailed = true;
      this.notificationService.show(`Beállítások betöltése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  saveBillingoSettings() {
    const payload: {
      enabled: boolean;
      api_key?: string | null;
      document_insert_type: BillingoDocumentInsertType;
      etsy_invoice_block_id?: number | null;
      shopify_hu_invoice_block_id?: number | null;
      shopify_en_invoice_block_id?: number | null;
    } = {
      enabled: this.billingoSettings.enabled,
      document_insert_type: this.billingoSettings.document_insert_type,
      etsy_invoice_block_id: this.billingoSettings.etsy_invoice_block_id,
      shopify_hu_invoice_block_id: this.billingoSettings.shopify_hu_invoice_block_id,
      shopify_en_invoice_block_id: this.billingoSettings.shopify_en_invoice_block_id,
    };

    if (this.clearApiKey) {
      payload.api_key = '';
    } else if (this.billingoApiKeyInput.trim().length) {
      payload.api_key = this.billingoApiKeyInput.trim();
    }

    this.savingBillingo = true;
    this.http.patch<BillingoSettingsResponse>('integrations/billingo-settings', payload).subscribe((result) => {
      this.savingBillingo = false;
      this.billingoSettings = result;
      this.rebuildBillingoBlockOptions();
      this.billingoApiKeyInput = '';
      this.clearApiKey = false;
      this.notificationService.show('Billingo beállítások mentve.', 'success');
      this.refreshActiveInvoiceProvider();
      this.refreshBillingoBlocks();
    }, (error) => {
      this.savingBillingo = false;
      this.notificationService.show(`Billingo beállítás mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  saveOkosKonyvelomSettings() {
    const payload: {
      enabled: boolean;
      base_url: string | null;
      api_key?: string | null;
      etsy_category_id: string | null;
      shopify_hu_category_id: string | null;
      shopify_en_category_id: string | null;
    } = {
      enabled: this.okosSettings.enabled,
      base_url: this.okosBaseUrlInput.trim() || null,
      etsy_category_id: this.okosSettings.etsy_category_id,
      shopify_hu_category_id: this.okosSettings.shopify_hu_category_id,
      shopify_en_category_id: this.okosSettings.shopify_en_category_id,
    };

    if (this.clearOkosApiKey) {
      payload.api_key = '';
    } else if (this.okosApiKeyInput.trim().length) {
      payload.api_key = this.okosApiKeyInput.trim();
    }

    this.savingOkosKonyvelom = true;
    this.http
      .patch<OkosKonyvelomSettingsResponse>('integrations/okoskonyvelom-settings', payload)
      .subscribe((result) => {
        this.savingOkosKonyvelom = false;
        this.okosSettings = result;
        this.okosBaseUrlInput = result.base_url || '';
        this.rebuildOkosCategoryOptions();
        this.okosApiKeyInput = '';
        this.clearOkosApiKey = false;
        this.notificationService.show('Okos Könyvelőm beállítások mentve.', 'success');
        this.refreshActiveInvoiceProvider();
        this.refreshOkosCategories();
      }, (error) => {
        this.savingOkosKonyvelom = false;
        this.notificationService.show(`Okos Könyvelőm beállítás mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
      });
  }

  private fetchOkosCategoriesRequest() {
    return this.http
      .get<OkosKonyvelomCategoryOption[]>('integrations/okoskonyvelom-settings/categories')
      .pipe(catchError(() => of([] as OkosKonyvelomCategoryOption[])));
  }

  private refreshOkosCategories() {
    this.fetchOkosCategoriesRequest().subscribe((categories) => {
      this.okosCategories = categories;
      this.rebuildOkosCategoryOptions();
    });
  }

  private refreshActiveInvoiceProvider() {
    this.http
      .get<{ provider: InvoiceProviderKey | null }>('integrations/invoice-provider')
      .pipe(catchError(() => of({ provider: null })))
      .subscribe((result) => {
        this.activeInvoiceProvider = result?.provider ?? null;
      });
  }

  /**
   * A beállított, de a lekért listában nem szereplő kategória-azonosító is opció legyen —
   * különben egy időközben törölt kategóriánál a select üresen jelenne meg, és a mentés
   * csendben kinullázná a besorolást.
   */
  private rebuildOkosCategoryOptions() {
    const options: OkosKonyvelomCategoryOption[] = [...this.okosCategories];
    const known = new Set(options.map((option) => option.id));
    for (const id of [
      this.okosSettings.etsy_category_id,
      this.okosSettings.shopify_hu_category_id,
      this.okosSettings.shopify_en_category_id,
    ]) {
      if (id && !known.has(id)) {
        options.push({ id, name: `Ismeretlen kategória (${id})` });
        known.add(id);
      }
    }
    this.okosCategoryOptions = options;
  }

  saveShippyProSettings() {
    const payload: {
      api_key?: string | null;
      api_orders_id?: number | null;
    } = {};
    const normalizedApiOrdersIdRaw = this.getNormalizedShippyProApiOrdersIdInput();

    if (this.clearShippyProApiKey) {
      payload.api_key = '';
    } else if (this.shippyProApiKeyInput.trim().length) {
      payload.api_key = this.shippyProApiKeyInput.trim();
    }

    if (this.clearShippyProApiOrdersId) {
      payload.api_orders_id = null;
    } else if (normalizedApiOrdersIdRaw.length) {
      const parsedApiOrdersId = Number(normalizedApiOrdersIdRaw);
      if (!Number.isInteger(parsedApiOrdersId) || parsedApiOrdersId <= 0) {
        this.notificationService.show('A ShippyPro API Orders ID csak pozitív egész szám lehet.', 'warning');
        return;
      }
      payload.api_orders_id = parsedApiOrdersId;
    }

    this.savingShippyPro = true;
    this.http.patch<ShippyProSettingsResponse>('integrations/shippypro-settings', payload).subscribe((result) => {
      this.savingShippyPro = false;
      this.shippyProSettings = result;
      this.shippyProApiKeyInput = '';
      this.clearShippyProApiKey = false;
      this.clearShippyProApiOrdersId = false;
      this.shippyProApiOrdersIdInput = result.api_orders_id || '';
      this.notificationService.show('ShippyPro beállítások mentve.', 'success');
    }, (error) => {
      this.savingShippyPro = false;
      this.notificationService.show(`ShippyPro beállítás mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  // --- E-mail (SMTP) ---

  private applyMailSettings(settings: MailSettingsResponse) {
    this.mailSettings = settings || this.mailSettings;
    this.mailForm = {
      host: this.mailSettings.host || '',
      port: this.mailSettings.port ?? '',
      secure: !!this.mailSettings.secure,
      username: this.mailSettings.username || '',
      password: '',
      from_email: this.mailSettings.from_email || '',
      from_name: this.mailSettings.from_name || '',
    };
    if (!this.mailTestTo) {
      this.mailTestTo = this.auth.user?.email || '';
    }
  }

  saveMailSettings() {
    const payload: any = {
      host: this.mailForm.host,
      port: `${this.mailForm.port}`.trim() === '' ? null : Number(this.mailForm.port),
      secure: !!this.mailForm.secure,
      username: this.mailForm.username,
      from_email: this.mailForm.from_email,
      from_name: this.mailForm.from_name,
    };
    if (this.clearMailPassword) {
      payload.password = '';
    } else if (this.mailForm.password) {
      payload.password = this.mailForm.password;
    }
    this.savingMail = true;
    this.http.patch<MailSettingsResponse>('integrations/mail-settings', payload).subscribe((result) => {
      this.savingMail = false;
      this.clearMailPassword = false;
      this.applyMailSettings(result);
      this.notificationService.show('E-mail beállítások mentve.', 'success');
    }, (error) => {
      this.savingMail = false;
      this.notificationService.show(`E-mail beállítás mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  sendMailTest() {
    const to = this.mailTestTo.trim();
    if (!to) {
      this.notificationService.show('Add meg a címzett e-mail címét.', 'warning');
      return;
    }
    this.sendingMailTest = true;
    this.http.post('integrations/mail-settings/test', { to }).subscribe(() => {
      this.sendingMailTest = false;
      this.notificationService.show(`Próbalevél elküldve ide: ${to}`, 'success');
    }, (error) => {
      this.sendingMailTest = false;
      this.notificationService.show(error?.error?.message || 'A próbalevél küldése nem sikerült.', 'danger');
    });
  }

  // --- 17TRACK ---

  fetchSeventeenQuota() {
    this.seventeenQuotaLoading = true;
    this.http.get<SeventeenTrackQuotaResponse>('integrations/seventeentrack-settings/quota').subscribe((result) => {
      this.seventeenQuota = result || { configured: false, quota: null };
      this.seventeenQuotaLoading = false;
    }, () => {
      this.seventeenQuota = { configured: this.seventeenSettings.has_api_key, quota: null, error: 'A kvóta nem érhető el.' };
      this.seventeenQuotaLoading = false;
    });
  }

  /** A 17TRACK kvóta-válasza mezőnként változó; a számokat emberi sorrá fűzzük. */
  get seventeenQuotaLabel(): string {
    const q = this.seventeenQuota?.quota;
    if (!q || typeof q !== 'object') { return ''; }
    const parts: string[] = [];
    const pick = (keys: string[], label: string) => {
      const key = keys.find((k) => q[k] !== undefined && q[k] !== null);
      if (key) { parts.push(`${label}: ${q[key]}`); }
    };
    pick(['quota_remain', 'remain', 'remaining', 'available'], 'maradt');
    pick(['quota_total', 'total'], 'összes');
    pick(['quota_used', 'used'], 'felhasznált');
    return parts.length ? parts.join(' · ') : JSON.stringify(q);
  }

  // --- Foxi (MI-munkatárs) ---

  foxiModelLabel(model: string): string {
    const labels: Record<string, string> = {
      'claude-sonnet-5-5': 'Claude Sonnet 5.5 — ajánlott, gyors (≈ 2 / 10 USD / M token)',
      'claude-opus-5-5': 'Claude Opus 5.5 — a legokosabb, kb. 2× ár',
      'claude-haiku-4-5': 'Claude Haiku 4.5 — a legolcsóbb, egyszerű kérdésekre',
    };
    return labels[model] || model;
  }

  private applyFoxiSettings(settings: FoxiSettingsResponse) {
    this.foxiSettings = settings;
    this.foxiModelInput = settings.model;
    this.foxiBudgetInput = settings.daily_budget_usd;
    this.foxiEnabledInput = settings.enabled;
  }

  saveFoxiSettings() {
    const payload: { api_key?: string; model: string; enabled: boolean; daily_budget_usd: number } = {
      model: this.foxiModelInput,
      enabled: this.foxiEnabledInput,
      daily_budget_usd: Number(this.foxiBudgetInput) || 0,
    };
    if (this.clearFoxiApiKey) {
      payload.api_key = '';
    } else if (this.foxiApiKeyInput.trim().length) {
      payload.api_key = this.foxiApiKeyInput.trim();
    }
    this.savingFoxi = true;
    this.http.patch<FoxiSettingsResponse>('agent/settings', payload).subscribe((result) => {
      this.savingFoxi = false;
      this.applyFoxiSettings(result);
      this.foxiApiKeyInput = '';
      this.clearFoxiApiKey = false;
      this.notificationService.show('Foxi beállításai mentve. 🦊', 'success');
    }, (error) => {
      this.savingFoxi = false;
      this.notificationService.show(`Foxi beállítás mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  saveSeventeenSettings() {
    const payload: { api_key?: string | null } = {};
    if (this.clearSeventeenApiKey) {
      payload.api_key = '';
    } else if (this.seventeenApiKeyInput.trim().length) {
      payload.api_key = this.seventeenApiKeyInput.trim();
    }
    if (!Object.keys(payload).length) {
      this.notificationService.show('Nincs mit menteni: adj meg új kulcsot vagy jelöld be a törlést.', 'warning');
      return;
    }
    this.savingSeventeen = true;
    this.http.patch<SeventeenTrackSettingsResponse>('integrations/seventeentrack-settings', payload).subscribe((result) => {
      this.savingSeventeen = false;
      this.seventeenSettings = result;
      this.seventeenApiKeyInput = '';
      this.clearSeventeenApiKey = false;
      this.notificationService.show('17TRACK beállítások mentve.', 'success');
      this.fetchSeventeenQuota();
    }, (error) => {
      this.savingSeventeen = false;
      this.notificationService.show(`17TRACK beállítás mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  // --- Jump & Hide webshop (Medusa) ---

  private applyMedusaSettings(settings: MedusaSettingsResponse) {
    this.medusaSettings = settings;
    this.medusaBaseUrlInput = settings.base_url || '';
    this.medusaEnabledInput = !!settings.enabled;
  }

  saveMedusaSettings() {
    const payload: { enabled: boolean; base_url: string; api_key?: string } = {
      enabled: this.medusaEnabledInput,
      base_url: this.medusaBaseUrlInput.trim(),
    };
    if (this.medusaApiKeyInput.trim().length) {
      payload.api_key = this.medusaApiKeyInput.trim();
    }
    this.savingMedusa = true;
    this.http.patch<MedusaSettingsResponse>('medusa/settings', payload).subscribe((result) => {
      this.savingMedusa = false;
      this.medusaApiKeyInput = '';
      this.applyMedusaSettings(result);
      this.notificationService.show('Webshop beállítások mentve.', 'success');
    }, (error) => {
      this.savingMedusa = false;
      this.notificationService.show(`Webshop beállítás mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  syncMedusaNow() {
    this.syncingMedusa = true;
    this.http.post<{ result: string; created?: number; updated?: number; message?: string }>('medusa/sync', {}).subscribe((result) => {
      this.syncingMedusa = false;
      if (result?.result === 'ok') {
        this.notificationService.show(`Webshop szinkron kész: ${result.created || 0} új, ${result.updated || 0} frissített rendelés.`, 'success');
      } else {
        this.notificationService.show(`Webshop szinkron sikertelen: ${result?.message || 'ismeretlen hiba'}`, 'danger');
      }
      this.http.get<MedusaSettingsResponse>('medusa/settings').subscribe((s) => (this.medusaSettings = s));
    }, (error) => {
      this.syncingMedusa = false;
      this.notificationService.show(`Webshop szinkron sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  // --- AfterShip ---

  /** A fiókban bekapcsolt futárok — ezt látja a felhasználó, hogy a Spring/Foxpost be van-e kapcsolva. */
  fetchAfterShipCouriers() {
    this.afterShipCouriersLoading = true;
    this.http.get<AfterShipCouriersResponse>('integrations/aftership-settings/couriers').subscribe((result) => {
      this.afterShipCouriers = result || { configured: false, couriers: [] };
      this.afterShipCouriersLoading = false;
    }, () => {
      this.afterShipCouriers = { configured: this.afterShipSettings.has_api_key, couriers: [], error: 'A futárlista nem érhető el.' };
      this.afterShipCouriersLoading = false;
    });
  }

  get afterShipCourierNames(): string {
    return (this.afterShipCouriers.couriers || []).map((c) => c.name).join(', ');
  }

  saveAfterShipSettings() {
    const payload: { api_key?: string | null; webhook_secret?: string | null } = {};
    if (this.clearAfterShipApiKey) {
      payload.api_key = '';
    } else if (this.afterShipApiKeyInput.trim().length) {
      payload.api_key = this.afterShipApiKeyInput.trim();
    }
    if (this.clearAfterShipWebhookSecret) {
      payload.webhook_secret = '';
    } else if (this.afterShipWebhookSecretInput.trim().length) {
      payload.webhook_secret = this.afterShipWebhookSecretInput.trim();
    }
    if (!Object.keys(payload).length) {
      this.notificationService.show('Nincs mit menteni: adj meg új kulcsot vagy jelöld be a törlést.', 'warning');
      return;
    }
    this.savingAfterShip = true;
    this.http.patch<AfterShipSettingsResponse>('integrations/aftership-settings', payload).subscribe((result) => {
      this.savingAfterShip = false;
      this.afterShipSettings = result;
      this.afterShipApiKeyInput = '';
      this.afterShipWebhookSecretInput = '';
      this.clearAfterShipApiKey = false;
      this.clearAfterShipWebhookSecret = false;
      this.notificationService.show('AfterShip beállítások mentve.', 'success');
      this.fetchAfterShipCouriers();
    }, (error) => {
      this.savingAfterShip = false;
      this.notificationService.show(`AfterShip beállítás mentése sikertelen! Részletek: ${error?.error?.message || error.message}`, 'danger');
    });
  }

  formatBillingoBlockLabel(block: BillingoInvoiceBlockOption): string {
    const prefix = block?.prefix?.trim();
    if (prefix) {
      return `${block.name} (${prefix})`;
    }
    return block?.name || `Tömb #${block?.id}`;
  }

  hasShippyProApiOrdersIdInput(): boolean {
    return this.getNormalizedShippyProApiOrdersIdInput().length > 0;
  }

  private getNormalizedShippyProApiOrdersIdInput(): string {
    if (this.shippyProApiOrdersIdInput === null || this.shippyProApiOrdersIdInput === undefined) {
      return '';
    }
    return `${this.shippyProApiOrdersIdInput}`.trim();
  }

  private fetchBillingoBlocksRequest() {
    return this.http.get<BillingoInvoiceBlockOption[]>('integrations/billingo-settings/blocks').pipe(
      catchError(() => of([])),
    );
  }

  private refreshBillingoBlocks() {
    this.fetchBillingoBlocksRequest().subscribe((blocks) => {
      this.billingoBlocks = blocks;
      this.rebuildBillingoBlockOptions();
    });
  }

  /** A beállított tömb-ID hiányozhat a Billingótól kapott listából (törölt tömb, hibás API kulcs,
   *  üres lista) — ilyenkor a select semmit sem mutatna. Pótoljuk egy „ismeretlen tömb" opcióval. */
  private rebuildBillingoBlockOptions() {
    const options: BillingoInvoiceBlockOption[] = [...this.billingoBlocks];
    const selectedIds = [
      this.billingoSettings.etsy_invoice_block_id,
      this.billingoSettings.shopify_hu_invoice_block_id,
      this.billingoSettings.shopify_en_invoice_block_id,
    ];
    selectedIds.forEach((id) => {
      if (id != null && !options.some((block) => block.id === id)) {
        options.push({ id, name: `Ismeretlen tömb (#${id})` });
      }
    });
    this.billingoBlockOptions = options;
  }
}
